import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

export default function EventVideo({ src, poster, caption, subtitle }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
      setStarted(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative rounded-3xl overflow-hidden border border-qh-line bg-black shadow-[0_40px_80px_-30px_rgba(10,10,10,0.35)] group"
      data-testid="event-video"
    >
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          playsInline
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay (fades out while playing) */}
        <div
          className={`absolute inset-0 transition-opacity duration-500 ${
            playing && started ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/40" />
          {(caption || subtitle) && (
            <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 text-white">
              {subtitle && (
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-qh-rose">
                  {subtitle}
                </div>
              )}
              {caption && (
                <div className="mt-1 font-display text-2xl sm:text-3xl font-medium leading-tight max-w-lg">
                  {caption}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center play button */}
        <button
          onClick={toggle}
          aria-label={playing ? "Video pausieren" : "Video abspielen"}
          className="absolute inset-0 flex items-center justify-center"
          data-testid="event-video-play"
        >
          <span
            className={`inline-flex items-center justify-center h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-qh-rose text-white shadow-[0_20px_50px_-10px_rgba(225,29,72,0.7)] ring-4 ring-white/90 transition-all duration-300 ${
              playing && started
                ? "opacity-0 scale-90"
                : "opacity-100 scale-100 hover:scale-105"
            }`}
          >
            {playing ? (
              <Pause className="w-8 h-8 fill-white" strokeWidth={0} />
            ) : (
              <Play
                className="w-8 h-8 fill-white translate-x-0.5"
                strokeWidth={0}
              />
            )}
          </span>
        </button>

        {/* Mute toggle (only while playing) */}
        {started && (
          <button
            onClick={toggleMute}
            aria-label={muted ? "Ton einschalten" : "Ton ausschalten"}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="event-video-mute"
          >
            {muted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
}
